import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import axiosInstance from '../../utils/axiosConfig';
import useTranslation from '../../utils/useTranslation';
import { formatDate } from '../ArticleModal';
import Reveal from '../Reveal';
import SectionViewAllLink from '../SectionViewAllLink';

const HOME_PROJECTS_LIMIT = 3;

export default function ProjectsSection({ innerRef }) {
  const { t, lang } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axiosInstance
      .get('public/projects/', { params: { lang } })
      .then((res) => {
        if (!cancelled) setItems(res.data?.projects || []);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  const shown = items.slice(0, HOME_PROJECTS_LIMIT);
  const hasMore = items.length > HOME_PROJECTS_LIMIT;
  const gridClass = shown.length <= 1 ? 'grid-1' : shown.length === 2 ? 'grid-2' : 'grid-3';

  const showViewAll = !loading && hasMore;

  return (
    <section className="section" id="projects" ref={innerRef}>
      <div className="container">
        <div className="projects-head">
          <h2 className="partners-title">
            <span className="partners-title-line" aria-hidden="true" />
            <span className="partners-title-text">{t('projects_title')}</span>
            <span className="partners-title-line" aria-hidden="true" />
          </h2>
          {showViewAll && (
            <SectionViewAllLink
              label={t('projects_view_all')}
              onClick={() => navigate('/projects')}
            />
          )}
        </div>

        {loading ? (
          <div className="spinner" />
        ) : items.length === 0 ? (
          <p className="muted-note">{t('empty_section')}</p>
        ) : (
          <>
            <div className={`grid ${gridClass}`}>
              {shown.map((item, i) => (
              <Reveal
                as="article"
                className="card card-clickable"
                key={item.id}
                delay={(i % 3) * 80}
                role="link"
                tabIndex={0}
                onClick={() => navigate(`/projects/${item.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/projects/${item.id}`);
                  }
                }}
              >
                {item.image_url ? (
                  <div className="card-media">
                    <div
                      className="card-media-blur"
                      style={{ backgroundImage: `url(${item.image_url})` }}
                      aria-hidden="true"
                    />
                    <img src={item.image_url} alt={item.title} loading="lazy" />
                  </div>
                ) : (
                  <div className="card-media card-media-empty" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 5h13v14H6a2 2 0 0 1-2-2V5z" />
                      <path d="M17 8h3v9a2 2 0 0 1-2 2" />
                      <line x1="7" y1="8.5" x2="14" y2="8.5" />
                      <line x1="7" y1="12" x2="14" y2="12" />
                    </svg>
                  </div>
                )}
                <div className="card-body">
                  {item.uploaded_date && (
                    <span className="card-date">{formatDate(item.uploaded_date, lang)}</span>
                  )}
                  <h3 className="card-title">{item.title}</h3>
                  {item.overview && <p className="card-excerpt">{item.overview}</p>}
                  <span className="news-card-cta" aria-hidden="true">
                    {t('read_more')}
                    <svg className="news-card-cta-arrow" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </span>
                </div>
              </Reveal>
            ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
